import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  TextField,
  Table,
  TableBody,
  AppBar,
  Modal,
  Toolbar,
  Typography,
  TableCell,
  TableContainer,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  TableHead,
  TableRow,
  Paper,
  Box,
  InputAdornment,
  TablePagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  styled,
  CircularProgress,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import LoadingBar from "react-top-loading-bar";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Tooltip from "@mui/material/Tooltip";
import base64 from "base64-js";
import { ClosedCaptionDisabledTwoTone } from "@mui/icons-material";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});


const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 20,
  padding: "8px 24px",
  textTransform: "none",
  fontWeight: 600,
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  },
}));

const InventoryLocation = () => {
  const [isMaster, setIsMaster] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(1);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [showUserGuide, setShowUserGuide] = useState(false);
  const navigate = useNavigate();
  const { encodedFormId } = useParams();
  const [locations, setLocations] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingBarProgress, setLoadingBarProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [authorization, setAuthorization] = useState(null);
  const [error, setError] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openResultDialog, setOpenResultDialog] = useState(false);
  const [resultMessage, setResultMessage] = useState("");
  const [isResultError, setIsResultError] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Decode the encodedFormId
  const decodeFormId = (encodedFormId) => {
    try {
      const bytes = base64.toByteArray(encodedFormId);
      return new TextDecoder().decode(bytes);
    } catch (error) {
      console.error("Error decoding formId:", error);
      return "";
    }
  };

  // Validate encodedFormId
 useEffect(() => {
  const isMasterUser = sessionStorage.getItem("is_Master") === "true";
  setIsMaster(isMasterUser);

  if (isMasterUser) {
    fetchBranches();
  } else {
    setSelectedBranchId(sessionStorage.getItem("branchId"));
  }
}, []);

useEffect(() => {
  if (encodedFormId) {
    Authorizer();
  }
}, [encodedFormId]);

useEffect(() => {
  if (encodedFormId && selectedBranchId) {
    fetchLocations();
  }
}, [encodedFormId, selectedBranchId]);

  const formId = decodeFormId(encodedFormId);

  const fetchBranches = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/Branch/GetBranch`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({}),
      });
      if (response.ok) {
        const data = await response.json();
        setBranches(data);
      } else {
        console.error("Failed to fetch branches");
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  const fetchLocations = async () => {
    try {
      setLoadingBarProgress(30);
      setIsLoading(true);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(
        `${apiUrl}/Inventory/locations/GetLocations`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionStorage.getItem("token"),
          },
          body: JSON.stringify({
            locationId: 0,
            locationName: "",
            branchID: isMaster ? selectedBranchId : sessionStorage.getItem("branchId"),
          }),
        }
      );

      if (response.ok) {
        const responseData = await response.json();
        setLocations(responseData.locations || []);
        setError("");
      } else {
        let errorMsg = "Failed to load locations";
        if (response.status === 401)
          errorMsg = "Unauthorized: Please log in again";
        else if (response.status === 403)
          errorMsg = "Forbidden: You lack permission to view locations";
        else if (response.status === 404) errorMsg = "Locations not found";
        else if (response.status >= 500)
          errorMsg = "Server error: Please try again later";
        setError(errorMsg);
        setLocations([]);
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
      setError("Network error: Unable to connect to the server");
      setLocations([]);
    } finally {
      setLoadingBarProgress(100);
      setIsLoading(false);
    }
  };

  const Authorizer = async () => {
    if (!formId) {
      setError("Invalid form ID for authorization.");
      return;
    }
    try {
      setLoadingBarProgress(30);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/CPanel/Module_Authorizer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          formId,
        }),
      });

      if (response.ok) {
        const responseData = await response.json();
        setAuthorization(responseData);
        const authorizationData = responseData[0];
        setCanEdit(authorizationData.uModify === 1);
        setCanDelete(authorizationData.uDelete === 1);
      } else {
        console.error("Authorization failed:", response.status);
        setError("Invalid authorization response.");
      }
    } catch (error) {
      console.error("API request error:", error);
      setError("An error occurred during authorization.");
    } finally {
      setLoadingBarProgress(100);
    }
  };

  const handleAddLocation = () => {
    navigate("/location/add", { state: { encodedFormId } });
  };

  const handleEditLocation = (locationID) => {
    if (!canEdit) {
      setResultMessage("You don’t have permission to edit locations.");
      setIsResultError(true);
      setOpenResultDialog(true);
      return;
    }
    navigate(`/location/edit/${locationID}`, { state: { encodedFormId } });
  };

  const handleOpenDeleteDialog = (location) => {
    setLocationToDelete(location);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setLocationToDelete(null);
    setIsDeleting(false);
  };

  const handleCloseResultDialog = () => {
    setOpenResultDialog(false);
    setResultMessage("");
    setIsResultError(false);
  };

  const handleDeleteLocation = async () => {
    if (!canDelete || !locationToDelete) {
      setResultMessage("You don’t have permission to delete locations.");
      setIsResultError(true);
      setOpenResultDialog(true);
      handleCloseDeleteDialog();
      return;
    }

    setIsDeleting(true);
    try {
      setLoadingBarProgress(30);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(
        `${apiUrl}/Inventory/locations/DeleteLocation/${locationToDelete.locationID}`,
        {
          method: "DELETE",
          headers: {
            Authorization: sessionStorage.getItem("token"),
          },
        }
      );

      if (response.ok) {
        setLocations(
          locations.filter(
            (location) => location.locationID !== locationToDelete.locationID
          )
        );
        setOpenDeleteDialog(false);
        setSnackbarMessage('Stock Location deleted successfully!');
        setOpenSnackbar(true);
      } else {
        let errorMsg = "Failed to delete location";
        if (response.status === 401)
          errorMsg = "Unauthorized: Please log in again";
        else if (response.status === 403)
          errorMsg = "Forbidden: You lack permission to delete locations";
        else if (response.status === 404) errorMsg = "Location not found";
        else if (response.status >= 500)
          errorMsg = "Server error: Please try again later";
        setResultMessage(errorMsg);
        setIsResultError(true);
        setOpenResultDialog(true);
      }
    } catch (error) {
      console.error("Error deleting location:", error);
      setResultMessage("Network error: Unable to connect to the server");
      setIsResultError(true);
      setOpenResultDialog(true);
    } finally {
      setLoadingBarProgress(100);
      setIsDeleting(false);
      handleCloseDeleteDialog();
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredLocations = locations.filter((location) =>
    location.locationName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedLocations = filteredLocations.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (error && !encodedFormId) {
    return (
      <div className="container mt-4">
        <Typography color="error">{error}</Typography>
      </div>
    );
  }

  return (
    <div className="container ">
      <LoadingBar
        progress={loadingBarProgress}
        color="rgb(152, 106, 182)"
        height={5}
      />

      
      <AppBar position="static" style={{ backgroundColor: "#0B1F3D"}}>
        <Toolbar sx={{justifyContent:'space-between'}}>
          <Typography variant="h4" component="div" style={{ fontWeight: 'bold' }}>
            Manage Stock Locations  <InfoIcon onClick={() => setShowUserGuide(true)} style={{ "opacity": "0.6" }} />
          </Typography>

  {/* Branch Dropdown — Master Users Only */}
    {isMaster && (
      <Box sx={{ position: "relative" }}>
        <FormControl size="small" sx={{ width: 220 }}>
          <InputLabel
            sx={{
              color: "rgba(255,255,255,0.6)",
              fontSize: "0.75rem",
              fontWeight: 500,
              letterSpacing: "0.6px",
              textTransform: "uppercase",
              top: "-2px",
              "&.Mui-focused": { color: "#64B5F6" },
              "&.MuiFormLabel-filled": { color: "rgba(255,255,255,0.7)" },
            }}
          >
            Branch
          </InputLabel>
          <Select
            value={selectedBranchId || ""}
            onChange={(e) => setSelectedBranchId(e.target.value)}
            label="Branch"
            startAdornment={
              <InputAdornment position="start">
                <LocationOnIcon sx={{ fontSize: 16, color: "rgba(255,255,255,0.5)", ml: 0.5 }} />
              </InputAdornment>
            }
            sx={{
              color: "#ffffff",
              fontWeight: 500,
              fontSize: "0.875rem",
              letterSpacing: "0.3px",
              backgroundColor: "rgba(255,255,255,0.07)",
              backdropFilter: "blur(8px)",
              borderRadius: "8px",
              transition: "background-color 0.2s ease, box-shadow 0.2s ease",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(255,255,255,0.15)",
                borderWidth: "1px",
                transition: "border-color 0.2s ease",
              },
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.11)",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(255,255,255,0.35)",
                },
              },
              "&.Mui-focused": {
                backgroundColor: "rgba(255,255,255,0.1)",
                boxShadow: "0 0 0 3px rgba(100,181,246,0.18)",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#64B5F6",
                  borderWidth: "1.5px",
                },
              },
              "& .MuiSelect-icon": {
                color: "rgba(255,255,255,0.5)",
                transition: "color 0.2s ease",
              },
              "&.Mui-focused .MuiSelect-icon": { color: "#64B5F6" },
              "& .MuiSelect-select": { py: 1.1 },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  mt: 0.5,
                  borderRadius: "10px",
                  border: "1px solid rgba(11,31,61,0.12)",
                  boxShadow: "0 8px 32px rgba(11,31,61,0.18), 0 2px 8px rgba(0,0,0,0.08)",
                  overflow: "hidden",
                  "& .MuiList-root": { py: 0.5 },
                },
              },
            }}
          >
            {branches.map((branch) => (
              <MenuItem
                key={branch.branchId}
                value={branch.branchId}
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  color: "#0B1F3D",
                  px: 2,
                  py: 1.1,
                  mx: 0.5,
                  borderRadius: "6px",
                  transition: "background-color 0.15s ease",
                  "&:hover": { backgroundColor: "rgba(11,31,61,0.07)" },
                  "&.Mui-selected": {
                    backgroundColor: "rgba(25,118,210,0.1)",
                    color: "#1565C0",
                    fontWeight: 600,
                    "&:hover": { backgroundColor: "rgba(25,118,210,0.15)" },
                  },
                }}
              >
                {branch.branchName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    )}

         
        </Toolbar>
      </AppBar>

      <Paper elevation={3} style={{ padding: 16, marginTop: 6 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 16,
          }}
        >
          <TextField
            variant="outlined"
            placeholder="Search locations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: 300, backgroundColor: "#f9f9f9" }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddLocation}
          // style={{ backgroundColor: "#0B1F3D" }}
          >
            Add Location
          </Button>
        </div>

        {isLoading ? (
          <Typography>Loading locations...</Typography>
        ) : (
          <>
            {error && (
              <Typography color="error" sx={{ mb: 2 }}>
                {error}
              </Typography>
            )}
            <TableContainer component={Paper} style={{ marginTop: 16 }}>
              <Table sx={{
                minWidth: 650,
                border: '1px solid #ccc',
                '& .MuiTableCell-root': {
                  borderRight: '1px solid #ccc',
                  fontSize: '1rem',
                },
                '& .MuiTableHead-root .MuiTableCell-root': {
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                },
              }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem' }}>
                      Location Name
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #ccc', borderRight: '1px solid #ccc', textAlign: 'center', fontWeight: 'bold', fontSize: '1.1rem' }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedLocations.length > 0 ? (
                    paginatedLocations.map((location) => (
                      <TableRow key={location.locationID} sx={{ borderTop: '1px solid #ccc' }}>
                        <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1.1rem', whiteSpace: "normal", wordWrap: "break-word" }}>
                          {location.locationName || "N/A"}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center', borderRight: '1px solid #ccc', fontSize: '1.1rem' }}>
                          <Tooltip title={canEdit ? "" : "Not authorized to edit"} arrow>
                            <span>
                              <Button
                                variant="contained"
                                color="warning"
                                onClick={() => handleEditLocation(location.locationID)}
                                disabled={!canEdit}
                                sx={{ marginRight: 1 }}
                                startIcon={<EditIcon />}
                              >
                                Edit
                              </Button>
                            </span>
                          </Tooltip>
                          <Tooltip title={canDelete ? "" : "Not authorized to delete"} arrow>
                            <span>
                              <Button
                                variant="contained"
                                color="error"
                                onClick={() => handleOpenDeleteDialog(location)}
                                disabled={!canDelete || isDeleting}
                                sx={{ marginRight: 1 }}
                                startIcon={<DeleteIcon />}
                              >
                                Delete
                              </Button>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={2} sx={{ textAlign: 'center' }}>No locations available</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>


            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredLocations.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Modal open={openDeleteDialog} onClose={ClosedCaptionDisabledTwoTone} aria-labelledby="delete-modal-title" aria-describedby="delete-modal-description">
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
            <Typography id="delete-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Delete Confirmation ⚠️
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ padding: '20px' }}>
            <Typography id="delete-modal-description" sx={{ mb: 2 }}>
              Are you sure you want to delete <strong>{locationToDelete?.locationName || "this location"}</strong>?
            </Typography>

            {/* Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={handleCloseDeleteDialog} variant="outlined" sx={{ mr: 1 }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleDeleteLocation}
              >
                Yes, Delete
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
      {/* Result Dialog for Success/Error */}
      <Dialog
        open={openResultDialog}
        onClose={handleCloseResultDialog}
        aria-labelledby="result-dialog-title"
      >
        <DialogTitle id="result-dialog-title">
          {isResultError ? "Error" : "Success"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{resultMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResultDialog} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

       <Dialog
            open={showUserGuide}
            onClose={() => setShowUserGuide(false)}
            maxWidth="sm"
            fullWidth
            sx={{
              "& .MuiDialog-paper": {
                borderRadius: "12px",
                boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                backgroundColor: "#f9fafb",
              },
            }}
          >
            <DialogTitle
              sx={{
                background: "linear-gradient(90deg, #1898c2 0%, #beab6c 100%)",
                color: "white",
                fontWeight: "bold",
                textAlign: "center",
                borderRadius: "12px 12px 0 0",
                py: 2,
              }}
            >
              Fee Deposit User Guide
            </DialogTitle>
            <DialogContent sx={{ padding: "24px" }}>
              <Typography
                variant="h6"
                sx={{
                  color: "#1898c2",
                  fontWeight: "600",
                  mb: 2,
                  mt: 1,
                  textAlign: "center",
                }}
              >
                Quick Steps to Manage Stock Location
              </Typography>
              <Box
                component="ul"
                sx={{
                  listStyleType: "none",
                  padding: 0,
                  "& li": {
                    display: "flex",
                    alignItems: "flex-start",
                    mb: 1.5,
                    fontSize: "0.9rem",
                    color: "#333",
                  },
                  "& li::before": {
                    content: '"\\2022"', // Bullet point
                    color: "#1898c2",
                    fontWeight: "bold",
                    display: "inline-block",
                    width: "1.5em",
                    mr: 1,
                    fontSize: "1.2rem",
                  },
                  "& strong": {
                    margin: "0 0.2em", // Add spacing around strong tags
                  },
                }}
              >
                <li><strong>Search</strong> locations by name using the search bar</li> <li><strong>Add</strong> new locations with the "Add Location" button</li> <li><strong>Edit</strong> existing locations (if you have permission)</li> <li><strong>Delete</strong> locations with confirmation (if you have permission)</li> <li>View all <strong>available locations</strong> in a paginated table</li> <li>Check your <strong>permissions</strong> for edit/delete actions</li> <li>Navigate between pages using the <strong>pagination controls</strong></li>


              </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
              <StyledButton
                onClick={() => setShowUserGuide(false)}
                color="primary"
                variant="contained"
                sx={{
                  backgroundColor: "#1898c2",
                  "&:hover": { backgroundColor: "#156f9a" },
                  borderRadius: "8px",
                  px: 4,
                }}
              >
                Close
              </StyledButton>
            </DialogActions>
          </Dialog>

    </div>
  );
};

export default InventoryLocation;
 